/**
 * Page-awareness for the AI assistant (B4). A lightweight context — not a
 * Redux slice — because it is transient, per-route UI state: the route
 * itself derives most of it, and the page mounted under that route can
 * enrich it with whatever else it knows (a dashboard's active campus
 * filter, a report's grouping) via `setPageContext`. Nothing here persists
 * across navigation on purpose; the enrichment resets the moment the route
 * changes so a stale `metric_id` from the page you just left can never leak
 * into a suggestion or a system prompt on the page you land on.
 *
 * `AiChatPanel` reads `usePageContext().pageContext` and sends it as
 * `context` on session create and on every `sendMessage`, merged with the
 * viewer's persona. Page headers (`DashboardHeader`, `MetricDetailPage`,
 * `ReportViewPage`) call `setPageContext({...})` to add what the route
 * alone can't know.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

const PageContextCtx = createContext(null)

const UUID_RE = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'

/**
 * Route-derived defaults: `page` (a short slug for the current section) and
 * whichever entity id the URL itself names. Mirrors the matching the old
 * inline `contextFromPath`/`pageFromPath` in AiChatPanel did before this
 * hook existed, extended with `metric_id` and `campus_id` (dashboards can
 * carry a campus in the URL when shared/embedded).
 *
 * @param {string} pathname
 * @returns {{page: string, dashboard_id?: string, metric_id?: string, report_id?: string, connection_id?: string}}
 */
function deriveFromPath(pathname) {
  const segments = pathname.split('/').filter(Boolean)
  if (!segments.length) return { page: 'home' }

  const ctx = { page: segments[0] }
  if (segments[0] === 'reports' && segments[1] && segments[1] !== 'new') {
    ctx.page = 'report'
  }

  // The integration-onboarding pages use the backend's own page names
  // (`integrations | measurements | getting_started`, A11) so the
  // suggestions endpoint returns their onboarding chips; the measurements
  // board and its mappings page also name their Rock connection.
  if (segments[0] === 'getting-started') ctx.page = 'getting_started'
  if (segments[0] === 'settings' && segments[1] === 'integrations') {
    const boardMatch = new RegExp(`^/settings/integrations/rock/(${UUID_RE})/measurements(?:/|$)`, 'i').exec(pathname)
    ctx.page = boardMatch ? 'measurements' : 'integrations'
    if (boardMatch) ctx.connection_id = boardMatch[1]
  }

  // The guided setups are setup surfaces (setupPages.js); Rock's names its connection.
  if (segments[0] === 'setup' && segments[1] === 'planning-center') ctx.page = 'setup_planning_center'
  if (segments[0] === 'setup' && segments[1] === 'rock') {
    ctx.page = 'setup_rock'
    const setupMatch = new RegExp(`^/setup/rock/(${UUID_RE})(?:/|$)`, 'i').exec(pathname)
    if (setupMatch) ctx.connection_id = setupMatch[1]
  }

  const dashboardMatch = new RegExp(`^/dashboards/(${UUID_RE})`, 'i').exec(pathname)
  if (dashboardMatch) ctx.dashboard_id = dashboardMatch[1]

  const metricMatch = new RegExp(`^/metrics/(${UUID_RE})`, 'i').exec(pathname)
  if (metricMatch) ctx.metric_id = metricMatch[1]

  const reportMatch = new RegExp(`^/reports/(${UUID_RE})`, 'i').exec(pathname)
  if (reportMatch) ctx.report_id = reportMatch[1]

  return ctx
}

export function PageContextProvider({ children }) {
  const location = useLocation()
  const [enrichment, setEnrichment] = useState({})
  const [enrichmentPath, setEnrichmentPath] = useState(location.pathname)

  // A page's enrichment (campus filter, date range, grouping) belongs to
  // that page only; leaving it behind on navigation is the whole point of
  // resetting here. Adjusted synchronously during render (the documented
  // React pattern for "reset state when a prop changes") rather than in a
  // trailing `useEffect`: an effect fires after the first post-navigation
  // commit, so that render would still merge the *previous* page's
  // enrichment with the *new* route's derived context for one frame -
  // exactly the stale-merge window this avoids.
  if (location.pathname !== enrichmentPath) {
    setEnrichmentPath(location.pathname)
    setEnrichment({})
  }

  const setPageContext = useCallback((patch) => {
    setEnrichment((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }))
  }, [])

  const pageContext = useMemo(
    () => ({ ...deriveFromPath(location.pathname), ...enrichment }),
    [location.pathname, enrichment]
  )

  const value = useMemo(() => ({ pageContext, setPageContext }), [pageContext, setPageContext])

  return <PageContextCtx.Provider value={value}>{children}</PageContextCtx.Provider>
}

/**
 * Reads the current page context and a setter pages can call to enrich it.
 * Usable outside a `PageContextProvider` (falls back to a route-less
 * `{ page: 'home' }` with a no-op setter) so tests and any page rendered
 * without the full app shell never crash.
 *
 * @returns {{ pageContext: object, setPageContext: (patch: object|Function) => void }}
 */
export function usePageContext() {
  const ctx = useContext(PageContextCtx)
  if (ctx) return ctx
  return { pageContext: { page: 'home' }, setPageContext: () => {} }
}
